// Parse a picked publish-queue folder (via <input webkitdirectory>) into a list
// of posts. A publish queue is a parent folder with one subfolder per post; each
// subfolder holds a video plus a `post.yaml` sidecar (see docs/FOLDER-IMPORT.md).

import yaml from "js-yaml";
import type { PlatformId, Visibility } from "../api";

const VIDEO_EXT = /\.(mp4|mov|webm)$/i;
const IMAGE_EXT = /\.(jpe?g|png|webp|gif)$/i;
const SIDECAR = /^post\.ya?ml$/i;

/** The values `visibility:` accepts, matching the composer's own control. */
const VISIBILITY_VALUES: readonly string[] = ["public", "unlisted", "private"];

/** The subset of `post.yaml` we read. Unknown keys are ignored. */
export interface PostYaml {
  video?: string;
  schedule?: string;
  /** `public` | `unlisted` | `private`. Typed loose because it is whatever the
   * sidecar happened to contain; read it through `postVisibility`. */
  visibility?: string;
  platforms?: {
    youtube?: { title?: string; description?: string };
    instagram?: { caption?: string; first_comment?: string };
    tiktok?: { caption?: string };
    x?: { text?: string };
    facebook?: { caption?: string };
  };
}

export interface ImportedPost {
  /** Post subfolder name, e.g. "post_01_bench" — used as the display label. */
  folder: string;
  /** The media file referenced by the sidecar (or the first one found). */
  mediaFile: File | null;
  data: PostYaml;
  /** Non-fatal issues (missing video, unparsed sidecar) surfaced to the user. */
  warnings: string[];
}

export interface FolderImportResult {
  root: string;
  posts: ImportedPost[];
  /** Folders that were skipped entirely (no media AND no sidecar), for a gentle
   * notice. A folder with a video but no `post.yaml` is NOT skipped — it becomes
   * a post whose platforms and captions are chosen on the site. */
  skipped: string[];
}

function segmentsOf(file: File): string[] {
  // webkitRelativePath is like "PUBLISH_QUEUE/post_01_bench/clip.mp4".
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
  return rel.split("/").filter(Boolean);
}

/**
 * Group a picked folder into posts. Two shapes are accepted:
 *
 *  1. A single post — the picked folder itself holds a video + `post.yaml`
 *     directly inside it. The folder IS the post.
 *  2. A publish queue — the picked folder is a parent with one subfolder per
 *     post, each subfolder holding its own video + `post.yaml`.
 *
 * A folder is a post when it contains a `post.yaml` OR a video/image. A sidecar
 * supplies platforms + captions; a bare media folder gets them on the site
 * instead (platforms are chosen batch-wide, captions typed or AI-generated).
 * Only a folder with neither media nor a sidecar is skipped.
 */
export async function parseFolder(fileList: FileList | File[]): Promise<FolderImportResult> {
  const files = Array.from(fileList);
  if (files.length === 0) return { root: "", posts: [], skipped: [] };

  const root = segmentsOf(files[0]!)[0] ?? "";

  const posts: ImportedPost[] = [];
  const skipped: string[] = [];

  // Shape 2 — bucket the deeper files by their immediate subfolder under the
  // root (segment index 1). Computed first so Shape 1 can tell whether the
  // picked folder is a lone post or a parent of post subfolders.
  const buckets = new Map<string, File[]>();
  for (const file of files) {
    const seg = segmentsOf(file);
    if (seg.length < 3) continue; // root-level file — handled by Shape 1
    const folder = seg[1]!;
    const list = buckets.get(folder) ?? [];
    list.push(file);
    buckets.set(folder, list);
  }

  // Whether any subfolder will actually become a post (has a sidecar or media).
  // Empty/junk subfolders (an OS artifact, a notes-only folder) don't count — so
  // a lone root video isn't silently discarded just because such a folder sits
  // beside it. Basing this on raw `buckets.size` was the bug.
  const hasRealSubfolderPosts = [...buckets.values()].some(
    (bucket) => bucket.some((f) => SIDECAR.test(baseName(f))) || bucket.some(isMedia),
  );

  // Shape 1 — files sitting directly inside the picked folder (segment index 1,
  // i.e. "root/file"). The picked folder is itself one post when it holds a
  // post.yaml, or — only when there are no real post subfolders — a bare video.
  // Stray root-level files alongside real subfolders (e.g. a README) are ignored.
  const rootBucket = files.filter((f) => segmentsOf(f).length === 2);
  if (rootBucket.some((f) => SIDECAR.test(baseName(f)))) {
    posts.push(await buildPost(root, rootBucket));
  } else if (!hasRealSubfolderPosts && rootBucket.some(isMedia)) {
    posts.push(await buildPost(root, rootBucket));
  }

  // Natural sort so "post_2" comes before "post_10".
  const folders = [...buckets.keys()].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );

  for (const folder of folders) {
    const bucket = buckets.get(folder)!;
    // A sidecar OR a bare video both make a post; only a folder with neither is
    // skipped (e.g. a stray folder of thumbnails with no clip).
    if (!bucket.some((f) => SIDECAR.test(baseName(f))) && !bucket.some(isMedia)) {
      skipped.push(folder);
      continue;
    }
    posts.push(await buildPost(folder, bucket));
  }

  return { root, posts, skipped };
}

/** Turn one post folder's files into a parsed post. A `post.yaml` is optional:
 * without one the post carries empty `data` (platforms + captions come from the
 * site) and a warning saying so. Shared by every shape above. */
async function buildPost(folder: string, bucket: File[]): Promise<ImportedPost> {
  const sidecar = bucket.find((f) => SIDECAR.test(baseName(f)));

  const warnings: string[] = [];
  let data: PostYaml = {};
  if (!sidecar) {
    warnings.push("No post.yaml — choose platforms and captions for this post below.");
  } else {
    try {
      const parsed: unknown = yaml.load(await sidecar.text());
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        data = parsed as PostYaml;
      } else if (parsed != null && parsed !== "") {
        // Parsed fine but isn't a key/value mapping (e.g. a list or bare scalar).
        warnings.push("post.yaml doesn't look like a key/value mapping.");
      }
    } catch (err) {
      warnings.push(
        `Couldn't parse post.yaml: ${err instanceof Error ? err.message : "invalid YAML"}`,
      );
    }
  }

  // An unusable `visibility:` is reported and dropped, never fatal: the post
  // simply falls back to the batch-wide choice, like one that never set it.
  if (data.visibility != null && postVisibility(data) === null) {
    warnings.push(
      `visibility: "${String(data.visibility)}" isn't public, unlisted or private — using the visibility chosen here instead.`,
    );
  }

  // Prefer the file named in the sidecar; fall back to the first video/image.
  const videos = bucket.filter((f) => VIDEO_EXT.test(baseName(f)));
  const named = data.video
    ? bucket.find((f) => baseName(f).toLowerCase() === data.video!.toLowerCase())
    : undefined;
  const mediaFile =
    named ?? videos[0] ?? bucket.find((f) => IMAGE_EXT.test(baseName(f))) ?? null;
  if (!mediaFile) {
    warnings.push("No video/image found in this folder.");
  } else if (data.video && !named) {
    warnings.push(`"${data.video}" not found — using ${baseName(mediaFile)} instead.`);
  } else if (!named && videos.length > 1) {
    warnings.push(`Multiple videos here — using ${baseName(mediaFile)}. Set "video:" to choose.`);
  }

  return { folder, mediaFile, data, warnings };
}

function baseName(file: File): string {
  const seg = segmentsOf(file);
  return seg[seg.length - 1] ?? file.name;
}

/** A video or image — the thing that, sidecar or not, makes a folder a post. */
function isMedia(file: File): boolean {
  const name = baseName(file);
  return VIDEO_EXT.test(name) || IMAGE_EXT.test(name);
}

/** Which platforms the sidecar provides text for (regardless of connection). */
export function platformsInPost(data: PostYaml): PlatformId[] {
  const p = data.platforms ?? {};
  const out: PlatformId[] = [];
  if (p.youtube) out.push("youtube");
  if (p.instagram) out.push("instagram");
  if (p.tiktok) out.push("tiktok");
  if (p.x) out.push("x");
  if (p.facebook) out.push("facebook");
  return out;
}

/** The visibility this post asks for, or `null` when it doesn't ask for one —
 * including when it asks for something that isn't a visibility at all. Callers
 * fall back to the creator's batch-wide choice in both cases, so a typo costs
 * one post its preference and never the import. */
export function postVisibility(data: PostYaml): Visibility | null {
  const raw = data.visibility;
  if (typeof raw !== "string") return null;
  const value = raw.trim().toLowerCase();
  return VISIBILITY_VALUES.includes(value) ? (value as Visibility) : null;
}

/** Convert an ISO-ish local datetime to the value a <input type=datetime-local>
 * expects ("YYYY-MM-DDTHH:mm"), or null if it isn't a usable future-capable string. */
export function toDatetimeLocal(iso: string | undefined): string | null {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.trim().match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : null;
}
